import { Helmet } from "react-helmet";

const SITE_NAME = "Lappy Used Laptops";
const SITE_URL = "https://lappylaptops.com";
const DEFAULT_DESCRIPTION =
  "Buy used and refurbished laptops in Namakkal with warranty, repair support, and accessories at Lappy Used Laptops.";
const DEFAULT_IMAGE = "/lappy_logo_png.png";
const DEFAULT_KEYWORDS =
  "used laptops Namakkal, refurbished laptops, laptop sales, laptop repair, laptop accessories, Lappy Laptops, Tamil Nadu";

const toAbsoluteUrl = (value) => {
  if (!value) return SITE_URL;
  if (/^https?:\/\//i.test(value)) return value;
  return `${SITE_URL}${value.startsWith("/") ? value : `/${value}`}`;
};

function SEO({
  title,
  description,
  url,
  image = DEFAULT_IMAGE,
  keywords = DEFAULT_KEYWORDS,
  author = SITE_NAME,
  type = "website",
  noindex = false,
  structuredData = null,
}) {
  const safeDescription = description || DEFAULT_DESCRIPTION;
  const safeTitle = title?.trim();
  const fullTitle =
    safeTitle && safeTitle.toLowerCase().includes(SITE_NAME.toLowerCase())
      ? safeTitle
      : safeTitle
      ? `${safeTitle} | ${SITE_NAME}`
      : SITE_NAME;

  const canonicalUrl = toAbsoluteUrl(url || "/");
  const imageUrl = toAbsoluteUrl(image || DEFAULT_IMAGE);
  const robotsContent = noindex ? "noindex, nofollow" : "index, follow";
  const extraSchemas = Array.isArray(structuredData)
    ? structuredData
    : structuredData
    ? [structuredData]
    : [];

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/products?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "ComputerStore",
    name: SITE_NAME,
    image: imageUrl,
    url: SITE_URL,
    telephone: "+918838619488",
    priceRange: "INR",
    address: [
      {
        "@type": "PostalAddress",
        streetAddress: "129 Salapalayam Road, Reddipatti",
        addressLocality: "Namakkal",
        addressRegion: "Tamil Nadu",
        postalCode: "637002",
        addressCountry: "IN",
      },
      {
        "@type": "PostalAddress",
        streetAddress: "27, Devangar Street",
        addressLocality: "Thathiengarpettai, Musiri-Tk",
        addressRegion: "Tamil Nadu",
        postalCode: "621214",
        addressCountry: "IN",
      },
    ],
    areaServed: {
      "@type": "State",
      name: "Tamil Nadu",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: "11.207586751094425",
      longitude: "78.22037987429323",
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ],
        opens: "09:00",
        closes: "20:00",
      },
    ],
    description: safeDescription,
  };

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={safeDescription} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={safeDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={safeDescription} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:site" content="@lappylaptops" />
      <meta name="twitter:creator" content="@lappylaptops" />

      <meta name="theme-color" content="#0b2c59" />
      <meta name="msapplication-TileColor" content="#0b2c59" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta
        name="apple-mobile-web-app-status-bar-style"
        content="black-translucent"
      />

      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      {extraSchemas.map((schema, index) => (
        <script key={`seo-schema-${index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export default SEO;
